import React from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
    userInfoSelectors,
} from '@src/ducks';
import { Permissions } from '@src/utils/permissions';
import { adminResourceURL } from '@src/utils/urlHelpers';

/**
 * EditAdminUrl Component
 *
 * Conditionally renders an edit link for admin users with appropriate permissions.
 * Opens the admin interface for editing the specified resource in a new tab.
 *
 * ## Features
 * - Conditional rendering based on user access level and permissions
 * - Automatic URL generation for admin resources
 * - Security attributes (target="_blank", rel="noopener noreferrer")
 * - Accessible link structure
 *
 * ## Permissions
 * - Requires user access level to be 'admin'
 * - Requires update permission for the specific resource type
 *
 * ## Props
 * - `id` (string|number): Resource identifier
 * - `resourceType` (string): Type of resource (e.g., 'ProductUpdate', 'Training')
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.id - Resource identifier
 * @param {string} props.resourceType - Type of resource to edit
 * @returns {React.ReactElement|null} - Edit link or null if unauthorized
 *
 * @example
 * <EditAdminUrl id={456} resourceType="Training" />
 */
const EditAdminUrl = ({ id, resourceType }) => {
    const accessLevel = useSelector(userInfoSelectors.getAccessLevel);
    const permissions = useSelector(userInfoSelectors.getPermissions);
    const href = adminResourceURL(resourceType, id);

    if (accessLevel !== 'admin' || Permissions(resourceType, 'update', permissions) !== true) {
        return null;
    }

    return (
        <a
            className="link-primary edit-admin-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
        >
            <span />
            <div className="edit-admin-text">
                Edit
            </div>
        </a>
    );
};

EditAdminUrl.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resourceType: PropTypes.string,
};

export default React.memo(EditAdminUrl);
